import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useFormOptions, useCreateTicket } from "@/hooks/useTickets";
import { useStorage } from "@/hooks/useStorage";
import {
  TicketFormData,
} from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2, Send, Paperclip, CheckCircle2, X, FileIcon, AlertCircle, Ticket, Search } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/utils";

const ticketSchema = z.object({
  requesterName: z.string().min(2, "Name must be at least 2 characters"),
  requesterPhone: z.string().max(20).optional(),
  requesterEmail: z.string().email("Invalid email address"),
  departmentId: z.string().min(1, "Please select a department"),
  account: z.string().min(1, "Account is required"),
  ticketTypeId: z.string().min(1, "Please select a ticket type"),
  priority: z.enum(["low", "medium", "high", "critical"]),
  description: z.string().min(10, "Description must be at least 10 characters"),
  relatedSystem: z.enum(["crm", "erp", "website", "other"]).optional(),
  expectedOutcome: z.string().optional(),
});

type FormValues = z.infer<typeof ticketSchema>;

export function SubmitTicketPage() {
  const navigate = useNavigate();
  const {
    departments,
    ticketTypes,
    isLoading: optionsLoading,
  } = useFormOptions();
  const createTicket = useCreateTicket();
  const { uploads, addFiles, removeFile, clearUploads, isUploading } = useStorage();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [trackingToken, setTrackingToken] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      requesterName: "",
      requesterPhone: "",
      requesterEmail: "",
      departmentId: "",
      account: "",
      ticketTypeId: "",
      priority: "medium",
      description: "",
      relatedSystem: undefined,
      expectedOutcome: "",
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const ticketData: TicketFormData = {
        requesterName: values.requesterName,
        ...(values.requesterPhone?.trim() && { requesterPhone: values.requesterPhone.trim() }),
        requesterEmail: values.requesterEmail,
        departmentId: values.departmentId,
        account: values.account,
        ticketTypeId: values.ticketTypeId,
        priority: values.priority,
        description: values.description,
        relatedSystem: values.relatedSystem,
        expectedOutcome: values.expectedOutcome,
        attachments: uploads
          .filter(u => u.status === 'completed' && u.attachmentId)
          .map(u => u.attachmentId!)
      };

      const ticket = await createTicket.mutateAsync(ticketData);

      // Clear form on success. Support both camelCase and snake_case from API.
      const token = (ticket as { trackingToken?: string }).trackingToken ?? (ticket as { tracking_token?: string }).tracking_token ?? null;
      form.reset();
      clearUploads();
      setTrackingToken(token);
      setIsSubmitted(true);
      toast.success("Ticket submitted successfully!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to submit ticket"));
    }
  };

  const handleCloseSuccessDialog = () => {
    form.reset();
    clearUploads();
    setIsSubmitted(false);
    setTrackingToken(null);
  };

  const handleTrackTicket = () => {
    if (trackingToken) {
      navigate(`/track/${trackingToken}`);
    }
  };

  return (
    <>
      <Dialog open={isSubmitted} onOpenChange={(open) => !open && handleCloseSuccessDialog()}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <CheckCircle2 className="h-7 w-7 text-success" />
            </div>
            <DialogTitle className="text-center">Ticket Submitted Successfully!</DialogTitle>
            <DialogDescription className="text-center">
              Your request has been received and will be reviewed by our tech team. A confirmation email has been sent to your email address.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={handleCloseSuccessDialog}
            >
              <Ticket className="mr-2 h-4 w-4" />
              Submit another ticket
            </Button>
            <Button
              className="w-full sm:w-auto"
              onClick={handleTrackTicket}
              disabled={!trackingToken}
            >
              <Search className="mr-2 h-4 w-4" />
              Track ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Submit a Support Request
        </h1>
        <p className="text-muted-foreground">
          Fill out the form below to submit a ticket to our tech team. We'll get
          back to you as soon as possible.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ticket Details</CardTitle>
          <CardDescription>
            Please provide as much detail as possible to help us resolve your
            issue quickly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="requesterName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="John Smith" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="requesterEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="john.smith@company.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="requesterPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="+1 (555) 123-4567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Department & Account */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Department Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="departmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select your department" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {departments.map((dept) => (
                              <SelectItem key={dept.id} value={dept.id}>
                                {dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="account"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account *</FormLabel>
                        <FormControl>
                          <Input placeholder="Account name or ID" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Ticket Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Ticket Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="ticketTypeId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Request Type *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select request type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {ticketTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select priority..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="relatedSystem"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Related System</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select system (if applicable)" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="crm">CRM</SelectItem>
                            <SelectItem value="erp">ERP</SelectItem>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please describe your issue or request in detail..."
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Include steps to reproduce the issue if applicable.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expectedOutcome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expected Outcome</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="What would you like to see as the resolution?"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Attachments */}
              <div className="space-y-3">
                <Label>Attachments</Label>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <label className={`flex items-center gap-2 px-4 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {isUploading ? "Uploading..." : "Add files"}
                      </span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                        disabled={isUploading}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      />
                    </label>
                  </div>
                  
                  {uploads.length > 0 && (
                    <ul className="space-y-2">
                      {uploads.map((upload, index) => (
                        <li
                          key={index}
                          className="flex flex-col p-3 bg-muted/50 border rounded-md gap-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 overflow-hidden">
                              <FileIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <span className="text-sm truncate font-medium">{upload.file.name}</span>
                              <span className="text-xs text-muted-foreground">({(upload.file.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => removeFile(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {upload.status === 'uploading' && (
                            <div className="space-y-1">
                              <Progress value={upload.progress} className="h-1.5" />
                              <p className="text-xs text-muted-foreground text-right">{upload.progress}%</p>
                            </div>
                          )}
                          
                          {upload.status === 'error' && (
                            <div className="flex items-center gap-1 text-destructive text-xs">
                              <AlertCircle className="h-3 w-3" />
                              <span>{upload.error || "Upload failed"}</span>
                            </div>
                          )}
                          
                          {upload.status === 'completed' && (
                            <div className="flex items-center gap-1 text-success text-xs">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>Ready</span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={createTicket.isPending || isUploading}
              >
                {createTicket.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Ticket
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
      </div>
    </>
  );
}
